import { isToday } from 'date-fns';
import { getFormattedDate, useFormattedRelativeDate } from '@frameio/utils';

type FormatTimestampArgs = {
  date: Date;
  includeTime?: boolean;
};

type FormatTimestamp = {
  timestamp: string | null | undefined;
};

/**
 * @description `useFormatTimestamp` - generate formatted timestamp for
 * notification cell footer.
 *
 * Examples:
 * - if notification occured today -> 10m
 * - if notification occured before today -> 5:38pm
 *
 * @param {Date} date - the timestamp as a Date Object to use to create
 * formatted timestamp
 *
 * @returns {string}
 **/
export const useFormatTimestamp = ({
  date,
  includeTime = true,
}: FormatTimestampArgs): FormatTimestamp => {
  const isDateToday = isToday(date);

  const formattedRelativeDate = useFormattedRelativeDate({
    date,
    showSuffix: true,
    truncate: true,
    skipInterval: true,
  });

  const formattedDateWithTime = getFormattedDate({
    date,
    returnDateParts: true,
  })?.formattedTime;

  const formattedDateWithoutTime = getFormattedDate({
    date,
    returnBothDateAndTime: includeTime,
  }).formattedDate;

  return isDateToday
    ? {
        timestamp: formattedRelativeDate, // i.e. 10m
      }
    : {
        timestamp: includeTime
          ? formattedDateWithTime
          : formattedDateWithoutTime, // i.e. Nov 25, 2020 at 5:38pm or Nov 25, 2020
      };
};
